import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { useBetaUserConfig } from '@/hooks/useBetaUserConfig';
import { CreditCard, Zap, Calendar, DollarSign, RefreshCw } from 'lucide-react';

const BetaUsageCard = () => {
  const { config, todayUsage, loading, error, fetchConfig, canPerformOperation } = useBetaUserConfig();

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            AI Usage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !config) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            AI Usage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <p className="text-muted-foreground mb-4">
              {error || 'Unable to load usage information'}
            </p>
            <Button onClick={fetchConfig} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getBadgeVariant = (tier: string) => {
    switch (tier) {
      case 'free': return 'secondary';
      case 'premium': return 'default';
      case 'unlimited': return 'destructive';
      default: return 'secondary';
    }
  };

  const creditsUsed = config.beta_tier === 'free' ? 100 - config.ai_credits_remaining : 0;
  const creditsProgress = config.beta_tier === 'free' ? (creditsUsed / 100) * 100 : 0;
  
  const dailyUsed = todayUsage?.operations_count || 0;
  const dailyProgress = (dailyUsed / config.daily_limit) * 100;
  
  const monthlySpent = config.monthly_spend_cents / 100; // Convert to dollars
  const monthlyBudget = config.monthly_budget_cents / 100;
  const monthlyProgress = (config.monthly_spend_cents / config.monthly_budget_cents) * 100;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            AI Usage
          </CardTitle>
          <Badge variant={getBadgeVariant(config.beta_tier)}>
            {config.beta_tier} tier
          </Badge>
        </div>
        <CardDescription>
          {canPerformOperation ? 'Ready for AI operations' : 'Limits reached - upgrade or wait for reset'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Credits Remaining (Free tier only) */}
        {config.beta_tier === 'free' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Credits Remaining
              </span>
              <span className="font-medium">
                {config.ai_credits_remaining} / 100
              </span>
            </div>
            <Progress value={100 - creditsProgress} className="h-2" />
          </div>
        )}

        {/* Daily Usage */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Daily Operations
            </span>
            <span className="font-medium">
              {dailyUsed} / {config.daily_limit === 999999 ? '∞' : config.daily_limit}
            </span>
          </div>
          {config.daily_limit !== 999999 && (
            <Progress value={dailyProgress} className="h-2" />
          )}
        </div>

        {/* Monthly Spend */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Monthly Spend
            </span>
            <span className="font-medium">
              ${monthlySpent.toFixed(2)} / ${monthlyBudget === 999999 ? '∞' : monthlyBudget.toFixed(2)}
            </span>
          </div>
          {config.monthly_budget_cents !== 999999 && (
            <Progress value={monthlyProgress} className="h-2" />
          )}
        </div>

        {/* Usage by Type */}
        {todayUsage?.operations_by_type && Object.keys(todayUsage.operations_by_type).length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Today's Operations</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {Object.entries(todayUsage.operations_by_type).map(([type, count]) => (
                <div key={type} className="flex justify-between bg-muted p-2 rounded">
                  <span className="capitalize">{type.replace(/_/g, ' ')}</span>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status */}
        <div className="pt-2 border-t">
          <div className={`text-sm ${canPerformOperation ? 'text-green-600' : 'text-red-600'}`}>
            {canPerformOperation ? (
              <>✓ AI operations available</>
            ) : (
              <>⚠ AI operations limited - {
                config.ai_credits_remaining <= 0 ? 'out of credits' :
                dailyUsed >= config.daily_limit ? 'daily limit reached' :
                config.monthly_spend_cents >= config.monthly_budget_cents ? 'monthly budget exceeded' :
                'unknown limit'
              }</>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BetaUsageCard;